import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Stack, Checkbox, Typography, Box,
  Button as MuiButton, CircularProgress,
} from '@mui/material';
import { useGetIdentity, useNotify, useDataProvider } from 'react-admin';
import { getToken } from '../../authentication/authProvider';
import { CustomerEventType } from '../../types';
import { getSlotsForRegion } from './documentSlots';

const API_URL = process.env.REACT_APP_PAPERMAN_API_URL || 'http://localhost:3001';
const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;

interface VerifiedDocument {
  id: string;
  verification_id: string;
  document_type: string;
  status: string;
}

interface Attachment {
  label: string;
  url: string;
  checked: boolean;
  /** Marks the verified POD/POC document. Drives whether verifiedDocumentId is sent. */
  isVerifiedDoc?: boolean;
  /** Slot wanted but no funeral_document found yet — kept visible so staff can see it's missing. */
  missing?: boolean;
  /** Shown only when "See all documents" is expanded. */
  extra?: boolean;
}

interface SendAttestationDialogProps {
  open: boolean;
  onClose: () => void;
  verifiedDoc: VerifiedDocument | null | undefined;
  funeral: any;
  primaryClient?: any;
  payingClient?: any;
  authorizedClient?: any;
  funeralDocs?: any[];
  onSent?: () => void;
}

export function SendAttestationDialog({
  open,
  onClose,
  verifiedDoc,
  funeral,
  primaryClient,
  payingClient,
  authorizedClient,
  funeralDocs = [],
  onSent,
}: SendAttestationDialogProps) {
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const dataProvider = useDataProvider();

  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [sending, setSending] = useState(false);
  const [showAllDocs, setShowAllDocs] = useState(false);

  // Pre-populate on open
  useEffect(() => {
    if (!open || !funeral || !verifiedDoc) return;
    const isFrench = funeral.language === 'fr';
    const userName = identity?.fullName || 'Cleo Team';
    const clientFirstName = primaryClient?.first_name || '';

    const primaryEmail = (primaryClient?.email || '').trim();
    const ccEmails = [payingClient?.email, authorizedClient?.email]
      .map((e) => (e || '').trim())
      .filter((e) => e && e.toLowerCase() !== primaryEmail.toLowerCase())
      .filter((e, i, arr) => arr.findIndex((x) => x.toLowerCase() === e.toLowerCase()) === i);

    setTo(primaryEmail);
    setCc(ccEmails.join(', '));
    setSubject('Cleo - Documents');

    let checklistUrl: string;
    let checklistLabel: string;
    if (funeral.region === 'ontario') {
      checklistUrl = 'https://dlwlfzjarwyddyxkahfw.supabase.co/storage/v1/object/public/files//Cleo%20Cremation%20-%20Checklist%20ON.pdf';
      checklistLabel = 'Cleo Cremation Checklist ON';
    } else {
      checklistUrl = isFrench
        ? 'https://dlwlfzjarwyddyxkahfw.supabase.co/storage/v1/object/public/files//Cleo%20-%20Liste%20de%20verification%20apres%20un%20deces%20QC.pdf'
        : 'https://dlwlfzjarwyddyxkahfw.supabase.co/storage/v1/object/public/files//Cleo%20Cremation%20-%20Checklist%20QC.pdf';
      checklistLabel = isFrench ? 'Cleo - Liste de verification QC' : 'Cleo Cremation Checklist QC';
    }

    let bodyText: string;
    if (funeral.region === 'ontario') {
      bodyText = `Hello ${clientFirstName},\n\nPlease find attached our Proof of Death document. This document can be used as valid proof of death with many institutions while waiting for the official documents to arrive.\n\nI have also included a checklist that may be helpful in settling the estate.\n\nIf you need anything else or have any questions, please don't hesitate to let me know.\n\nThank you,\n${userName}`;
    } else {
      bodyText = isFrench
        ? `Bonjour ${clientFirstName},\n\nVous trouverez ci-joint notre document d'attestation de décès. Ce document peut être utilisé comme preuve de décès valide auprès de plusieurs institutions en attendant l'arrivée des documents officiels.\n\nJ'ai également inclus une liste de vérification qui pourrait vous être utile dans le règlement de la succession.\n\nSi vous avez besoin de quoi que ce soit d'autre ou si vous avez des questions, n'hésitez pas à me le faire savoir.\n\nMerci,\n${userName}`
        : `Hello ${clientFirstName},\n\nPlease find attached our Attestation of Death document. This document can be used as valid proof of death with many institutions while waiting for the official documents to arrive.\n\nI have also included a checklist that may be helpful in settling the estate.\n\nIf you need anything else or have any questions, please don't hesitate to let me know.\n\nThank you,\n${userName}`;
    }
    setBody(bodyText);

    const next: Attachment[] = [
      { label: `Attestation (${verifiedDoc.document_type})`, url: '', checked: true, isVerifiedDoc: true },
      { label: checklistLabel, url: checklistUrl, checked: true },
    ];

    const preneedDoc = funeral.region === 'quebec'
      ? funeralDocs.find((d: any) => d.document_type === 'preneed_confirmation')
      : null;
    if (funeral.region === 'quebec') {
      if (preneedDoc) {
        next.push({
          label: 'Preneed Confirmation',
          url: `${baseUrl}/documents/funeral-document/${preneedDoc.id}/download`,
          checked: true,
        });
      } else {
        next.push({
          label: 'Preneed Confirmation',
          url: '',
          checked: false,
          missing: true,
        });
      }
    }

    // Append the rest of the case's funeral_documents as optional extras, hidden
    // behind "See all documents". Exclude preneed (already shown above).
    const slotLabelByType = new Map(
      getSlotsForRegion(funeral.region || 'quebec').map((s) => [s.type, s.label]),
    );
    const preneedId = preneedDoc?.id;
    funeralDocs
      .filter((d: any) => d.id !== preneedId)
      .forEach((d: any) => {
        const label = slotLabelByType.get(d.document_type) || d.original_filename || d.document_type;
        next.push({
          label,
          url: `${baseUrl}/documents/funeral-document/${d.id}/download`,
          checked: false,
          extra: true,
        });
      });

    setAttachments(next);
    setShowAllDocs(false);
  }, [open, funeral, verifiedDoc, primaryClient, payingClient, authorizedClient, funeralDocs, identity]);

  const handleSend = async () => {
    if (!funeral || !verifiedDoc) return;
    setSending(true);
    try {
      const token = await getToken();
      const podAttached = attachments.some((a) => a.isVerifiedDoc && a.checked);
      const additional = attachments
        .filter((a) => a.checked && !a.isVerifiedDoc && a.url)
        .map((a) => ({ url: a.url, filename: a.label + '.pdf' }));

      const payload: any = {
        to,
        subject,
        body,
      };
      if (podAttached) payload.verifiedDocumentId = verifiedDoc.id;
      if (cc.trim()) payload.cc = cc;
      if (additional.length > 0) payload.attachments = additional;

      const response = await fetch(`${baseUrl}/funerals/${funeral.id}/certificate-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message || 'Failed to send email');
      }

      // ATTESTATION_SENT drives timeline step tracking. Email itself is
      // logged to communications by MailService — don't duplicate as EMAIL.
      try {
        await dataProvider.create('customer_events', {
          data: {
            funeral_id: Number(funeral.id),
            timestamp: new Date().toISOString(),
            type: CustomerEventType.ATTESTATION_SENT,
            recipient_type: 'client' as const,
            recipient_id: primaryClient?.id ? Number(primaryClient.id) : undefined,
            recipient_name: primaryClient
              ? `${primaryClient.first_name || ''} ${primaryClient.last_name || ''}`.trim()
              : to,
            contact_method: 'email',
            contact_detail: to + (cc.trim() ? `, CC: ${cc}` : ''),
            subject,
            message: body,
            user_id: identity?.id ? Number(identity.id) : undefined,
            user_name: identity?.fullName || 'System',
          },
        });
      } catch {
        /* event logging is best-effort */
      }

      notify('Attestation sent successfully', { type: 'success' });
      onClose();
      onSent?.();
    } catch (e: any) {
      notify(e.message || 'Failed to send attestation', { type: 'error' });
    } finally {
      setSending(false);
    }
  };

  const previewAttachment = async (att: Attachment) => {
    try {
      if (att.isVerifiedDoc && verifiedDoc) {
        const token = await getToken();
        const res = await fetch(`${baseUrl}/documents/${verifiedDoc.id}/download`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const blob = await res.blob();
          window.open(URL.createObjectURL(blob), '_blank');
        }
      } else if (att.url.includes('/funeral-document/')) {
        const token = await getToken();
        const res = await fetch(att.url, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
          const blob = await res.blob();
          window.open(URL.createObjectURL(blob), '_blank');
        }
      } else if (att.url) {
        window.open(att.url, '_blank');
      }
    } catch {
      /* ignore */
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Send Attestation to Client
        {verifiedDoc && (
          <Typography variant="caption" display="block" color="text.secondary" sx={{ fontFamily: 'monospace', mt: 0.25 }}>
            {verifiedDoc.verification_id}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <TextField label="To" value={to} onChange={(e) => setTo(e.target.value)} fullWidth required />
          <TextField label="CC" value={cc} onChange={(e) => setCc(e.target.value)} fullWidth placeholder="Optional, comma-separated" />
          <TextField label="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} fullWidth required />
          <TextField label="Body" value={body} onChange={(e) => setBody(e.target.value)} fullWidth multiline minRows={5} required />
          <Box sx={{ bgcolor: '#f9fafb', p: 2, borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 500 }}>Attachments:</Typography>
            {attachments.map((att, idx) => {
              if (att.extra && !showAllDocs) return null;
              return (
                <Box key={`${att.label}-${idx}`} sx={{ display: 'flex', alignItems: 'center', mb: 1.5, justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Checkbox
                      checked={att.checked}
                      disabled={att.missing}
                      onChange={(e) => {
                        setAttachments((prev) =>
                          prev.map((a, i) => (i === idx ? { ...a, checked: e.target.checked } : a)),
                        );
                      }}
                      color="primary"
                      sx={{ color: '#5c68e0', '&.Mui-checked': { color: '#5c68e0' } }}
                    />
                    <Typography sx={{ fontWeight: 500 }}>{att.label}</Typography>
                    {att.missing && (
                      <Typography
                        component="span"
                        sx={{ ml: 1, px: 1, py: 0.25, borderRadius: 1, fontSize: '0.75rem', fontWeight: 500, bgcolor: '#fef3c7', color: '#b45309' }}
                      >
                        Missing
                      </Typography>
                    )}
                  </Box>
                  {!att.missing && (att.isVerifiedDoc || att.url) && (
                    <MuiButton
                      variant="outlined"
                      color="primary"
                      onClick={() => { void previewAttachment(att); }}
                      sx={{ borderRadius: 6, px: 3, py: 0.5, color: '#5c68e0', borderColor: '#5c68e0', textTransform: 'uppercase', fontWeight: 500, fontSize: '0.875rem' }}
                    >
                      Preview
                    </MuiButton>
                  )}
                </Box>
              );
            })}
            {attachments.some((a) => a.extra) && (
              <MuiButton
                size="small"
                onClick={() => setShowAllDocs((v) => !v)}
                sx={{ color: '#5c68e0', textTransform: 'none', fontWeight: 500, fontSize: '0.8125rem', mt: 0.5 }}
              >
                {showAllDocs ? 'Hide other documents' : `See all documents (${attachments.filter((a) => a.extra).length})`}
              </MuiButton>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <MuiButton onClick={onClose} disabled={sending}>Cancel</MuiButton>
        <MuiButton onClick={handleSend} variant="contained" color="primary" disabled={sending || !to.trim()}>
          {sending ? <CircularProgress size={20} /> : 'Send'}
        </MuiButton>
      </DialogActions>
    </Dialog>
  );
}
